import { pointer, select, Selection } from 'd3-selection'
import { format as d3format } from 'd3-format'
import { brushX, brushY } from 'd3-brush'
import BaseChart from './BaseChart'
import buildScale from './buildScale'
import { css } from 'emotion'
import axisBuilder from './axis'
import selectionToSpan from './selectionToSpan'
import changeAxisOpacity from './changeAxisOpacity'
import Tooltip from './Tooltip'
import pointService from 'live/services/charts/point'
import { scaleLinear, ScaleLinear, ScaleTime } from 'd3-scale'
import Legend from './Legend'
import timeFormatter from './timeFormat'
import { AXIS_SIZE } from 'live/services/charts/axis.styled'
import SpanService from 'live/services/span'

export const axisCss = css`
    .tick {
        text {
            font-family: 'Lucida Grande', 'Lucida Sans Unicode', Arial, Helvetica, sans-serif;
            font-size: 11px;
            fill: #666;

            body.dark-theme & {
                fill: #ddd;
            }
        }
    }

    .domain,
    .tick line {
        stroke: var(--gray-300);
    }
`

const mainCss = css`
    height: 100%;
    width: 100%;
    display: flex;
    user-select: none;

    .contents {
        width: 100%;
        height: 100%;
        position: relative;
        flex: 1;
        svg {
            text {
                font-size: 11px;
                font-family: 'Lucida Grande', 'Lucida Sans Unicode', Arial, Helvetica, sans-serif;
            }
            text.mark {
                fill: #666;
                body.dark-theme & {
                    fill: #fff;
                }
            }
            line.mark {
                stroke-width: 1px;
                stroke: var(--gray-400);
                body.dark-theme & {
                    stroke: #ccd6eb;
                }
            }
        }
    }
`

const axisWidthScale = scaleLinear([100, 200], [55, 82]).clamp(true)

export default abstract class TrackChart<T> extends BaseChart<T> {
    public _main: Selection<HTMLDivElement, unknown, null, undefined>
    protected _legend: Selection<HTMLDivElement, unknown, null, undefined>
    protected legend_: Legend
    protected _contents: Selection<HTMLDivElement, unknown, null, undefined>
    protected _svg: Selection<HTMLDivElement, unknown, null, undefined>

    protected innerWidth: number
    protected innerHeight: number

    protected tooltip: Tooltip<State>

    protected scale: ScaleLinear<[number, number], [number, number]>

    private mouseoverPoint: (p: { x: number | null; chart: typeof Object }) => null

    private subscription = Function.prototype

    init(): void {
        this.mouseoverPoint = (e: MouseEvent, chart: any): void => {
            try {
                if (e && e.target && chart !== this) {
                    const x = e.target.x
                    const value = this.scale(x)
                    const xValue = this.isVertical ? -10 : value
                    const yValue = !this.isVertical ? this.innerHeight : value
                    this._onMouseMove([xValue, yValue], this.scale)
                }
            } catch (e) {
                console.error(e)
            }
        }
        this.leave = () => this.onMouseLeave()

        const hasNewApi = Boolean(pointService.currentPoint$)

        if (hasNewApi) {
            this.subscription = pointService?.currentPoint$?.subscribe?.(p => {
                if (p.x === null) {
                    return this.onMouseLeave()
                }

                const value = this.scale(p.x)
                const xValue = this.isVertical ? -10 : value
                const yValue = !this.isVertical ? this.innerHeight : value
                this._onMouseMove([xValue, yValue], this.scale)
            })
        } else {
            pointService.addEventListener('mouseover', this.mouseoverPoint)
            pointService.addEventListener('mouseleave', this.leave)
        }

        this.el.classed('track-chart', true)

        this._main = this.el
            .selectAll(`div.${mainCss}`)
            .data([null])
            .join('div')
            .classed(mainCss, true)
            .classed('vertical', this.isVertical)
            .classed('horizontal', !this.isVertical)
            .style('flex-direction', this.isVertical ? 'column' : 'row')

        this._legend = this._main
            .selectAll('div.track-header')
            .data([null])
            .join('div')
            .classed('track-header', true)
            .style('position', 'relative')

        this._contents = this._main.append('div').classed('contents', true)

        const svg = this._contents.append('svg') as Selection<HTMLDivElement, unknown, null, undefined>
        if (svg) {
            this._svg = svg
                .style('z-index', 1)
                .style('position', 'absolute')
                .attr('height', '100%')
                .attr('width', '100%')
        }
    }

    get legendSize(): number {
        return this.numberOfVisibleAxes * AXIS_SIZE
    }

    updateLegendSize(): void {
        return this._legend
            .style('height', this.isVertical ? `${this.legendSize}px` : 'auto')
            .style('width', this.isVertical ? 'auto' : `${this.legendSize}px`)
    }

    updateLayout(): void {
        this.el
            .selectAll(`div.${mainCss}`)
            .data([null])
            .join('div')
            .classed(mainCss, true)
            .classed('vertical', this.isVertical)
            .classed('horizontal', !this.isVertical)
            .style('flex-direction', this.isVertical ? 'column' : 'row')

        this._main
            .selectAll('div.track-header')
            .data([null])
            .join('div')
            .classed('track-header', true)
            .style('position', 'relative')

        this.updateLegendSize()
        const node = this.el.node()
        if (node) {
            const { width, height } = node.getBoundingClientRect()

            this.innerWidth = Math.max(
                width - this.margin.left - (this.isVertical ? this.axisSize : this.margin.right + this.legendSize),
                0
            )
            this.innerHeight = Math.max(
                0,
                height - this.margin.top - this.margin.bottom - (this.isVertical ? this.legendSize : this.axisSize)
            )
        }
    }

    buildScale(data: any): ScaleLinear<number, number> | ScaleTime<number, number> {
        return buildScale(data, this.isVertical ? this.innerHeight : this.innerWidth, this.spanPickerType)
    }

    get yAxisHeight(): number {
        return 28
    }

    get legend() {}

    get inner(): { top: number; left: number; right?: number; bottom: number } {
        return {
            top: this.margin.top,
            left: this.isVertical && !this.internalAxis ? this.margin.left + this.axisSize : this.margin.left,
            bottom: this.isVertical ? this.margin.top : this.margin.top + this.innerHeight
        }
    }

    get axisSize(): number {
        // horizontal axis
        if (!this.isVertical) {
            return 36
        }

        if (this.isVertical && (this.internalAxis || this.hideXAxis)) {
            return 0
        }

        return axisWidthScale(this.width)
    }

    abstract onMouseMove(mouse: [number, number], scale: ScaleLinear): void

    _onMouseMove(mouse: [number, number], scale: ScaleLinear, updateAll = false): void {
        const format = this.spanPickerType === 'time' ? timeFormatter : d3format('.2f')

        const x = mouse[this.isVertical ? 1 : 0]

        const xOffset = this.isVertical ? this.margin.top : this.margin.left

        const value = scale.invert(x)

        if (updateAll) {
            this.highlightAll(value)
        }

        if (isNaN(value)) {
            return
        }

        const xWithOffset = x + xOffset

        // TODO: rename to crosshair, its a xAxis property
        this._svg
            .selectAll('line.mark')
            .style('visibility', 'visible')
            .attr('shape-rendering', 'crispEdges')
            .attr('x1', this.isVertical ? this.margin.left + this.axisSize - 7 : xWithOffset)
            .attr('x2', this.isVertical ? this.margin.left + this.axisSize + this.innerWidth : xWithOffset)
            .attr('y1', this.isVertical ? xWithOffset : this.margin.top)
            .attr('y2', this.isVertical ? xWithOffset : this.margin.top + this.innerHeight)

        this._svg
            .selectAll('text.mark')
            .style('visibility', 'visible')
            .attr('text-anchor', this.isVertical ? (this.internalAxis ? 'start' : 'end') : 'middle')
            .attr('alignment-baseline', this.isVertical ? 'middle' : 'hanging')
            .attr('x', this.isVertical ? this.margin.left + (this.internalAxis ? 10 : this.axisSize - 10) : xWithOffset)
            .attr(
                'y',
                this.isVertical ? (this.internalAxis ? -9 : 0) + xWithOffset : this.margin.top + this.innerHeight + 9
            )
            // .text(format(value))
            .call(s => {
                const text = format(value)
                if (
                    (this.isVertical && this.internalAxis) ||
                    !this.isVertical ||
                    (this.isVertical && this.axisSize > 65)
                ) {
                    s.text(text)

                    return
                }

                const tokens = text.trim().split(' ')

                if (tokens.length === 2 && (tokens[1] === 'PM' || tokens[1] === 'AM')) {
                    s.selectAll('tspan')
                        .data(tokens)
                        .join('tspan')
                        // .attr('dx', -8)
                        .attr('dy', (d, i) => (i === 0 ? 0 : '1.1em'))
                        .attr('dx', (d, i) => (i === 0 ? 0 : '-1.51em'))
                        .text(d => d)
                } else {
                    s.text(text)
                }
            })

        this._svg.selectAll('g.xAxis .tick text').call(changeAxisOpacity, xWithOffset, scale, this.spanPickerType)

        if (typeof this.onMouseMove === 'function') {
            this.onMouseMove(mouse, scale)
        }
    }

    onMouseEnter(): void {}

    onMouseLeave(): void {
        this._svg.selectAll('g.xAxis').selectAll('.tick text').style('opacity', 1)
        this._svg.selectAll('.mark').style('visibility', 'hidden')
        if (this.tooltip) {
            this.tooltip.hide()
        }
        if (this.legend_) {
            this.legend_.clearHover()
        }
    }

    updateSpan(span): void {
        super.updateSpan(span)
        this._svg.selectAll('.mark').style('visibility', 'hidden')
    }

    get internalAxis(): boolean {
        return false
    }

    abstract renderLegend(data: T): void

    render(data: T, configs): void {
        this.options.cfg = configs.widget.jsonConfig
        this.updateLayout()

        if (!data) {
            return
        }
        this.renderLegend(data)

        const scale = this.buildScale(data)
        this.scale = scale
        // draw axis
        if ((!this.hideXAxis && this.isVertical) || !this.isVertical) {
            this._svg
                .selectAll('g.xAxis')
                .data([null])
                .join('svg:g')
                .classed('xAxis', true)
                .classed(axisCss, true)
                .attr('transform', `translate(${this.inner.left} ${this.inner.bottom})`)
                .call(
                    axisBuilder,
                    scale,
                    this.isVertical,
                    this.spanPickerType,
                    this.axisSize,
                    this.internalAxis,
                    data.span,
                    SpanService.isRealtime(data.start?.info?.span.text)
                )
        }
        const brush = (this.isVertical ? brushY() : brushX()).on('end', brushed).extent([
            [0, 0],
            [this.innerWidth, this.innerHeight]
        ])

        function brushed(event): void {
            if (event.selection) {
                const span = selectionToSpan(event.selection, scale)
                self.updateSpan(span)
                select(this).call(brush.move, null)
            }
        }

        this._svg.selectAll('line.mark').data([null]).join('line').attr('class', 'mark')

        this._svg.selectAll('text.mark').data([null]).join('text').attr('class', 'mark')

        const self = this

        const brushSelection = this._svg.selectAll('g.brush')

        brushSelection
            .data([null])
            .join('g')
            .attr('class', 'brush')
            .attr('transform', `translate(${this.inner.left} ${this.inner.top})`)
            .call(brush)
            .selectAll('.overlay') // using .brush.overlay to monitor current itens
            .on('mouseenter', () => this.onMouseEnter())
            .on('mouseleave', () => this.onMouseLeave())
            .on('mousemove', event => {
                const mouse = pointer(event, brushSelection.node())
                this._onMouseMove(mouse, scale, true)
            })
    }

    get dataHeight(): number {
        return this.isVertical ? this.innerWidth : this.innerHeight
    }

    get dataWidth(): number {
        return this.isVertical ? this.innerHeight : this.innerWidth
    }

    grid() {
        return this._svg.selectAll('g.data').selectAll('g.grid').data([null]).join('g').lower().classed('grid', true)
    }

    plotHorizontalLines(yScale: ScaleLinear, points: any[]) {
        this.grid()
            .selectAll('line.horizontal')
            .data(points)
            .join('line')
            .classed('horizontal', true)
            .attr('shape-rendering', 'crispEdges')
            .attr('stroke', '#e6e6e6')
            .attr('stroke-width', '1px')
            .attr('y1', d => yScale(d))
            .attr('y2', d => yScale(d))
            .attr('x1', 0)
            .attr('x2', this.dataWidth)
    }

    plotVerticalLines(xScale: ScaleLinear, points: any[]) {
        this.grid()
            .selectAll('line.vertical')
            .data(points)
            .join('line')
            .classed('vertical', true)
            .attr('shape-rendering', 'crispEdges')
            .attr('stroke', '#e6e6e6')
            .attr('stroke-width', '1px')
            .attr('x1', d => xScale(d))
            .attr('x2', d => xScale(d))
            .attr('y1', 0)
            .attr('y2', this.dataHeight)
    }

    destroy(): void {
        if (this._main) {
            this._main.remove()
        }
        try {
            pointService.removeEventListener('mouseover', this.mouseoverPoint)
            pointService.removeEventListener('mouseleave', this.leave)

            this.subscription?.unsubscribe?.()
        } catch (e) {
            console.error(e)
        }
        if (this.tooltip) {
            this.tooltip.destroy()
        }
    }
}
